// Pure connection-editor draft state + submission building (agent.md module
// plan; extracted from AgentConnections.tsx so the 800-line hard cap holds —
// a seam, not a decision change). Owns the ConnectionFormDraft shape shared
// by the interface field groups, the draft round-trip from a stored
// connection (advanced HTTP fields carried verbatim so a rename or auth
// change never wipes stored config), validation, and the live config
// preview. No React, no network.
import type { components } from "../../../api/schema";
import {
  isDirectWebsocketConfig,
  isHttpConfig,
  isLiveKitConfig,
  isPipecatCloudWebrtcConfig,
  isPipecatCloudWebsocketConfig,
  isTwilioConfig,
} from "./connectionConfig";
import { parseJsonObject, parseOptionalJsonObject } from "./connectionJson";
import { containsSensitiveJsonKey, parseHeaders } from "./httpHeaders";
import {
  buildWebsocketConfig,
  websocketConfigPreview,
  websocketDraftFields,
} from "./websocketConnectionDraft";

type ConnectionConfig = components["schemas"]["ConnectionConfig"];
type ConnectionCapability =
  components["schemas"]["ConnectionCapabilityResponse"];
type ConnectionInitiator = components["schemas"]["ConnectionInitiator"];
type ConnectionResponse = components["schemas"]["ConnectionResponse"];
type ConnectionTransport = components["schemas"]["ConnectionTransport"];
type HttpAuthType = components["schemas"]["HttpAuthType"];
type HttpConnectionConfig = components["schemas"]["HttpConnectionConfig"];
type LiveKitConnectionConfig = components["schemas"]["LiveKitConnectionConfig"];
type TelephonyCodec = components["schemas"]["TelephonyCodec"];
type WebsocketAudioEnvelope = components["schemas"]["WebsocketAudioEnvelope"];
type WebsocketAuthType = components["schemas"]["WebsocketAuthType"];
type WebsocketConnectionConfig =
  components["schemas"]["WebsocketConnectionConfig"];

// The Interface select's value space: only running transports are authorable
// (§2.13; sip/legacy-websocket rows render read-only).
export type InterfaceKind =
  | "http"
  | "livekit_chat"
  | "livekit_voice"
  | "pipecat_chat"
  | "pipecat_voice"
  | "twilio"
  | "websocket_chat"
  | "websocket_voice";

export interface ConnectionFormSubmission {
  config: ConnectionConfig;
  displayName: string;
  initiator: ConnectionInitiator;
  transport: ConnectionTransport;
}

export interface ConnectionFormDraft {
  accountSid: string;
  authTokenSecretRef: string;
  displayName: string;
  fromNumber: string;
  headers: string;
  httpAuthType: HttpAuthType;
  // Opaque carry-through of the advanced HTTP fields the form has no controls
  // for yet: they are preserved verbatim on edit so a rename or auth change
  // never silently wipes a stored init_request/request_shaping/retry/timeout.
  httpInitRequest: HttpConnectionConfig["init_request"];
  httpRequestShaping: HttpConnectionConfig["request_shaping"];
  httpRetry: HttpConnectionConfig["retry"];
  httpSecretRef: string;
  httpTimeoutMs: HttpConnectionConfig["timeout_ms"];
  httpUrl: string;
  initiator: ConnectionInitiator;
  interfaceKind: InterfaceKind;
  livekitAgentEndEquals: string;
  livekitAgentEndField: string;
  livekitAgentName: string;
  livekitCredentialsRef: string;
  livekitEchoEquals: string;
  livekitEchoField: string;
  livekitInboundTextPath: string;
  livekitInboundTypeField: string;
  livekitInboundTypeValues: string;
  livekitMaxMessageBytes: string;
  livekitOutboundPattern: string;
  livekitSampleRateHz: string;
  livekitServerUrl: string;
  livekitTopic: string;
  pipecatAgentEndEquals: string;
  pipecatAgentEndField: string;
  pipecatAgentName: string;
  pipecatApiKeyRef: string;
  pipecatBody: string;
  pipecatEchoEquals: string;
  pipecatEchoField: string;
  pipecatInboundTextPath: string;
  pipecatInboundTypeField: string;
  pipecatInboundTypeValues: string;
  pipecatOutboundPattern: string;
  toNumber: string;
  websocketAgentEndSignal: WebsocketConnectionConfig["agent_end_signal"];
  websocketAuthType: WebsocketAuthType;
  websocketCoalesceWindowMs: WebsocketConnectionConfig["coalesce_window_ms"];
  websocketCodec: "" | TelephonyCodec;
  websocketDiscovery: WebsocketConnectionConfig["discovery"];
  websocketEnvelope: "" | WebsocketAudioEnvelope;
  websocketHandshake: WebsocketConnectionConfig["handshake"];
  websocketHeaders: string;
  websocketInbound: WebsocketConnectionConfig["inbound"];
  websocketKeepalivePingMs: WebsocketConnectionConfig["keepalive_ping_ms"];
  websocketOutboundPattern: WebsocketConnectionConfig["outbound_pattern"];
  websocketReconnect: WebsocketConnectionConfig["reconnect"];
  websocketSampleRateHz: string;
  websocketSecretRef: string;
  websocketSubprotocol: WebsocketConnectionConfig["subprotocol"];
  websocketUrl: string;
}

// Shared props contract for the interfaceFields/ transport field groups.
export interface InterfaceFieldsProps {
  draft: ConnectionFormDraft;
  onDraftChange: (draft: ConnectionFormDraft) => void;
}

export type ConnectionSubmissionResult =
  | { error: string; ok: false }
  | { ok: true; value: ConnectionFormSubmission };

type LiveKitCommonResult =
  | { error: string; ok: false }
  | {
      ok: true;
      value: Pick<
        LiveKitConnectionConfig,
        "agent_name" | "credentials_ref" | "provider" | "server_url"
      >;
    };

export function liveKitCommonFields(
  draft: ConnectionFormDraft,
): LiveKitCommonResult {
  const serverUrl = draft.livekitServerUrl.trim();
  const credentialsRef = draft.livekitCredentialsRef.trim();
  const agentName = draft.livekitAgentName.trim();
  if (!serverUrl.startsWith("wss://")) {
    return { error: "LiveKit server URL must start with wss://.", ok: false };
  }
  if (credentialsRef.length === 0 || agentName.length === 0) {
    return {
      error: "LiveKit credential and agent dispatch name are required.",
      ok: false,
    };
  }
  return {
    ok: true,
    value: {
      agent_name: agentName,
      credentials_ref: credentialsRef,
      provider: "livekit",
      server_url: serverUrl,
    },
  };
}

export function buildConnectionSubmission(
  draft: ConnectionFormDraft,
  websocketVoiceCapability?: ConnectionCapability,
): ConnectionSubmissionResult {
  const displayName = draft.displayName.trim();
  if (displayName.length === 0) {
    return { error: "Display name is required.", ok: false };
  }

  if (draft.interfaceKind === "http") {
    const url = draft.httpUrl.trim();
    if (!url.startsWith("https://")) {
      return { error: "Endpoint URL must start with https://.", ok: false };
    }
    const secretRef = draft.httpSecretRef.trim();
    if (draft.httpAuthType !== "none" && secretRef.length === 0) {
      return {
        error: "Secret reference is required for bearer and api-key auth.",
        ok: false,
      };
    }
    const headers = parseHeaders(draft.headers);
    if (!headers.ok) {
      return headers;
    }
    return {
      ok: true,
      value: {
        config: {
          auth: {
            secret_ref: draft.httpAuthType === "none" ? null : secretRef,
            type: draft.httpAuthType,
          },
          headers: headers.value,
          // Preserve stored advanced fields on edit; create-mode drafts carry
          // null, so this stays null for new connections.
          init_request: draft.httpInitRequest,
          provider: "direct",
          request_shaping: draft.httpRequestShaping,
          retry: draft.httpRetry,
          timeout_ms: draft.httpTimeoutMs,
          url,
        },
        displayName,
        initiator: "marker",
        transport: "http",
      },
    };
  }

  if (
    draft.interfaceKind === "websocket_chat" ||
    draft.interfaceKind === "websocket_voice"
  ) {
    const config = buildWebsocketConfig(draft, websocketVoiceCapability);
    if (!config.ok) {
      return config;
    }
    return {
      ok: true,
      value: {
        config: config.value,
        displayName,
        initiator: "marker",
        transport: "websocket",
      },
    };
  }

  if (
    draft.interfaceKind === "livekit_voice" ||
    draft.interfaceKind === "livekit_chat"
  ) {
    const common = liveKitCommonFields(draft);
    if (!common.ok) {
      return common;
    }
    if (draft.interfaceKind === "livekit_voice") {
      const sampleRate = Number(draft.livekitSampleRateHz);
      if (sampleRate !== 48_000) {
        return {
          error: "LiveKit WebRTC sample rate must be 48000 Hz.",
          ok: false,
        };
      }
      return {
        ok: true,
        value: {
          config: {
            ...common.value,
            audio: { sample_rate_hz: sampleRate },
            data: null,
          },
          displayName,
          initiator: "marker",
          transport: "webrtc",
        },
      };
    }

    const outbound = parseJsonObject(
      draft.livekitOutboundPattern,
      "Outbound pattern",
    );
    if (!outbound.ok) {
      return outbound;
    }
    const topic = draft.livekitTopic.trim();
    const endField = draft.livekitAgentEndField.trim();
    const endEquals = draft.livekitAgentEndEquals.trim();
    const maxMessageBytes = Number(draft.livekitMaxMessageBytes);
    if (
      topic.length === 0 ||
      endField.length === 0 ||
      endEquals.length === 0 ||
      !Number.isInteger(maxMessageBytes) ||
      maxMessageBytes < 1 ||
      maxMessageBytes > 15_360
    ) {
      return {
        error:
          "Topic, agent-end match, and maximum message bytes (1-15360) are required.",
        ok: false,
      };
    }
    const typeField = draft.livekitInboundTypeField.trim();
    const typeValues = draft.livekitInboundTypeValues
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    if ((typeField.length === 0) !== (typeValues.length === 0)) {
      return {
        error: "Inbound type field and type values must be set together.",
        ok: false,
      };
    }
    const echoField = draft.livekitEchoField.trim();
    const echoEquals = draft.livekitEchoEquals.trim();
    if ((echoField.length === 0) !== (echoEquals.length === 0)) {
      return {
        error: "Echo-filter field and value must be set together.",
        ok: false,
      };
    }
    return {
      ok: true,
      value: {
        config: {
          ...common.value,
          audio: null,
          data: {
            agent_end_signal: { equals: endEquals, field: endField },
            inbound: {
              echo_filter:
                echoField.length > 0
                  ? { equals: echoEquals, field: echoField }
                  : null,
              text_path: draft.livekitInboundTextPath.trim() || null,
              type_field: typeField || null,
              type_values: typeValues.length > 0 ? typeValues : null,
            },
            max_message_bytes: maxMessageBytes,
            outbound_pattern: outbound.value,
            topic,
          },
        },
        displayName,
        initiator: "marker",
        transport: "webrtc",
      },
    };
  }

  if (
    draft.interfaceKind === "pipecat_voice" ||
    draft.interfaceKind === "pipecat_chat"
  ) {
    const agentName = draft.pipecatAgentName.trim();
    const apiKeyRef = draft.pipecatApiKeyRef.trim();
    if (agentName.length === 0 || apiKeyRef.length === 0) {
      return {
        error: "Pipecat Cloud credential and agent name are required.",
        ok: false,
      };
    }
    const body = parseOptionalJsonObject(
      draft.pipecatBody,
      "Initialization body",
    );
    if (!body.ok) {
      return body;
    }
    if (body.value !== null && containsSensitiveJsonKey(body.value)) {
      return {
        error:
          "Initialization body must not contain raw secrets; use the credential selector.",
        ok: false,
      };
    }
    const bodyBytes =
      body.value === null
        ? 0
        : new TextEncoder().encode(JSON.stringify(body.value)).byteLength;
    const bodyLimit =
      draft.interfaceKind === "pipecat_voice" ? 1_000_000 : 4_000;
    if (bodyBytes > bodyLimit) {
      return {
        error: `Initialization body exceeds ${String(bodyLimit)} bytes.`,
        ok: false,
      };
    }
    if (draft.interfaceKind === "pipecat_voice") {
      return {
        ok: true,
        value: {
          config: {
            agent_name: agentName,
            api_key_ref: apiKeyRef,
            body: body.value,
            provider: "pipecat_cloud",
          },
          displayName,
          initiator: "marker",
          transport: "webrtc",
        },
      };
    }

    const outbound = parseJsonObject(
      draft.pipecatOutboundPattern,
      "Outbound pattern",
    );
    if (!outbound.ok) {
      return outbound;
    }
    const textPath = draft.pipecatInboundTextPath.trim();
    const endField = draft.pipecatAgentEndField.trim();
    const endEquals = draft.pipecatAgentEndEquals.trim();
    if (
      textPath.length === 0 ||
      endField.length === 0 ||
      endEquals.length === 0
    ) {
      return {
        error: "Inbound text path and agent-end match are required.",
        ok: false,
      };
    }
    const typeField = draft.pipecatInboundTypeField.trim();
    const typeValues = draft.pipecatInboundTypeValues
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    if ((typeField.length === 0) !== (typeValues.length === 0)) {
      return {
        error: "Inbound type field and type values must be set together.",
        ok: false,
      };
    }
    const echoField = draft.pipecatEchoField.trim();
    const echoEquals = draft.pipecatEchoEquals.trim();
    if ((echoField.length === 0) !== (echoEquals.length === 0)) {
      return {
        error: "Echo-filter field and value must be set together.",
        ok: false,
      };
    }
    return {
      ok: true,
      value: {
        config: {
          agent_end_signal: { equals: endEquals, field: endField },
          agent_name: agentName,
          api_key_ref: apiKeyRef,
          body: body.value,
          inbound: {
            echo_filter:
              echoField.length > 0
                ? { equals: echoEquals, field: echoField }
                : null,
            text_path: textPath,
            type_field: typeField || null,
            type_values: typeValues.length > 0 ? typeValues : null,
          },
          outbound_pattern: outbound.value,
          provider: "pipecat_cloud",
        },
        displayName,
        initiator: "marker",
        transport: "websocket",
      },
    };
  }

  const fromNumber = draft.fromNumber.trim();
  const toNumber = draft.toNumber.trim();
  const accountSid = draft.accountSid.trim();
  const authTokenSecretRef = draft.authTokenSecretRef.trim();
  if (
    fromNumber.length === 0 ||
    toNumber.length === 0 ||
    accountSid.length === 0 ||
    authTokenSecretRef.length === 0
  ) {
    return {
      error:
        "From number, To number, Account SID, and secret reference are required.",
      ok: false,
    };
  }

  return {
    ok: true,
    value: {
      config: {
        account_sid: accountSid,
        auth_token_secret_ref: authTokenSecretRef,
        codec: null,
        from_number: fromNumber,
        provider: "twilio",
        signaling_headers: null,
        to_number: toNumber,
        trigger: null,
      },
      displayName,
      initiator: draft.initiator,
      transport: "telephony",
    },
  };
}

export function emptyDraft(): ConnectionFormDraft {
  return {
    accountSid: "",
    authTokenSecretRef: "",
    displayName: "",
    fromNumber: "",
    headers: "{}",
    httpAuthType: "none",
    httpInitRequest: null,
    httpRequestShaping: null,
    httpRetry: null,
    httpSecretRef: "",
    httpTimeoutMs: null,
    httpUrl: "https://",
    initiator: "marker",
    interfaceKind: "http",
    livekitAgentEndEquals: "done",
    livekitAgentEndField: "type",
    livekitAgentName: "",
    livekitCredentialsRef: "",
    livekitEchoEquals: "",
    livekitEchoField: "",
    livekitInboundTextPath: "text",
    livekitInboundTypeField: "",
    livekitInboundTypeValues: "",
    livekitMaxMessageBytes: "15360",
    livekitOutboundPattern: JSON.stringify(
      { text: "{{text}}", type: "message" },
      null,
      2,
    ),
    livekitSampleRateHz: "48000",
    livekitServerUrl: "wss://",
    livekitTopic: "marker.chat",
    pipecatAgentEndEquals: "done",
    pipecatAgentEndField: "type",
    pipecatAgentName: "",
    pipecatApiKeyRef: "",
    pipecatBody: "",
    pipecatEchoEquals: "",
    pipecatEchoField: "",
    pipecatInboundTextPath: "text",
    pipecatInboundTypeField: "",
    pipecatInboundTypeValues: "",
    pipecatOutboundPattern: JSON.stringify(
      { text: "{{text}}", type: "message" },
      null,
      2,
    ),
    toNumber: "",
    websocketAgentEndSignal: null,
    websocketAuthType: "none",
    websocketCoalesceWindowMs: null,
    websocketCodec: "",
    websocketDiscovery: null,
    websocketEnvelope: "",
    websocketHandshake: null,
    websocketHeaders: "{}",
    websocketInbound: null,
    websocketKeepalivePingMs: null,
    websocketOutboundPattern: null,
    websocketReconnect: null,
    websocketSampleRateHz: "",
    websocketSecretRef: "",
    websocketSubprotocol: null,
    websocketUrl: "wss://",
  };
}

export function draftFromConnection(
  connection: ConnectionResponse,
): ConnectionFormDraft {
  const draft = emptyDraft();
  const config = connection.config;
  draft.displayName = connection.display_name;
  draft.initiator = connection.initiator;

  if (connection.transport === "http" && isHttpConfig(config)) {
    return {
      ...draft,
      headers: JSON.stringify(config.headers ?? {}, null, 2),
      httpAuthType: config.auth.type,
      // Carry the advanced fields through unchanged -- the form cannot edit
      // them, so an edit-mode save must round-trip whatever was stored.
      httpInitRequest: config.init_request ?? null,
      httpRequestShaping: config.request_shaping ?? null,
      httpRetry: config.retry ?? null,
      httpSecretRef: config.auth.secret_ref ?? "",
      httpTimeoutMs: config.timeout_ms ?? null,
      httpUrl: config.url,
      interfaceKind: "http",
    };
  }

  if (connection.transport === "websocket" && isDirectWebsocketConfig(config)) {
    return {
      ...draft,
      ...websocketDraftFields(config),
    };
  }

  if (isTwilioConfig(config)) {
    return {
      ...draft,
      accountSid: config.account_sid,
      authTokenSecretRef: config.auth_token_secret_ref,
      fromNumber: config.from_number,
      interfaceKind: "twilio",
      toNumber: config.to_number,
    };
  }

  if (isLiveKitConfig(config)) {
    const isVoice = config.audio !== null;
    return {
      ...draft,
      interfaceKind: isVoice ? "livekit_voice" : "livekit_chat",
      livekitAgentName: config.agent_name,
      livekitCredentialsRef: config.credentials_ref,
      livekitServerUrl: config.server_url,
      livekitSampleRateHz: String(config.audio?.sample_rate_hz ?? 48_000),
      livekitTopic: config.data?.topic ?? "marker.chat",
      livekitOutboundPattern: JSON.stringify(
        config.data?.outbound_pattern ?? { text: "{{text}}" },
        null,
        2,
      ),
      livekitInboundTextPath: config.data?.inbound.text_path ?? "",
      livekitInboundTypeField: config.data?.inbound.type_field ?? "",
      livekitInboundTypeValues:
        config.data?.inbound.type_values?.join(", ") ?? "",
      livekitEchoField: config.data?.inbound.echo_filter?.field ?? "",
      livekitEchoEquals: config.data?.inbound.echo_filter?.equals ?? "",
      livekitAgentEndField: config.data?.agent_end_signal.field ?? "type",
      livekitAgentEndEquals: config.data?.agent_end_signal.equals ?? "done",
      livekitMaxMessageBytes: String(config.data?.max_message_bytes ?? 15_360),
    };
  }

  if (connection.transport === "webrtc" && isPipecatCloudWebrtcConfig(config)) {
    return {
      ...draft,
      interfaceKind: "pipecat_voice",
      pipecatAgentName: config.agent_name,
      pipecatApiKeyRef: config.api_key_ref,
      pipecatBody:
        config.body == null ? "" : JSON.stringify(config.body, null, 2),
    };
  }

  if (
    connection.transport === "websocket" &&
    isPipecatCloudWebsocketConfig(config)
  ) {
    return {
      ...draft,
      interfaceKind: "pipecat_chat",
      pipecatAgentEndEquals: config.agent_end_signal?.equals ?? "done",
      pipecatAgentEndField: config.agent_end_signal?.field ?? "type",
      pipecatAgentName: config.agent_name,
      pipecatApiKeyRef: config.api_key_ref,
      pipecatBody:
        config.body == null ? "" : JSON.stringify(config.body, null, 2),
      pipecatEchoEquals: config.inbound.echo_filter?.equals ?? "",
      pipecatEchoField: config.inbound.echo_filter?.field ?? "",
      pipecatInboundTextPath: config.inbound.text_path ?? "",
      pipecatInboundTypeField: config.inbound.type_field ?? "",
      pipecatInboundTypeValues: config.inbound.type_values?.join(", ") ?? "",
      pipecatOutboundPattern: JSON.stringify(config.outbound_pattern, null, 2),
    };
  }

  return draft;
}

// Live preview for the "Stored config JSON" block in create mode: best-effort
// (unparseable JSON fields degrade to empty objects, never throw).
export function configPreview(draft: ConnectionFormDraft): ConnectionConfig {
  if (draft.interfaceKind === "http") {
    return {
      auth: {
        secret_ref:
          draft.httpAuthType === "none" ? null : draft.httpSecretRef.trim(),
        type: draft.httpAuthType,
      },
      headers: null,
      init_request: null,
      provider: "direct",
      request_shaping: null,
      retry: null,
      timeout_ms: null,
      url: draft.httpUrl.trim(),
    };
  }

  if (
    draft.interfaceKind === "websocket_chat" ||
    draft.interfaceKind === "websocket_voice"
  ) {
    return websocketConfigPreview(draft);
  }

  if (draft.interfaceKind === "livekit_voice") {
    return {
      agent_name: draft.livekitAgentName.trim(),
      audio: { sample_rate_hz: Number(draft.livekitSampleRateHz) || 48_000 },
      credentials_ref: draft.livekitCredentialsRef.trim(),
      data: null,
      provider: "livekit",
      server_url: draft.livekitServerUrl.trim(),
    };
  }

  if (draft.interfaceKind === "livekit_chat") {
    const outbound = parseJsonObject(
      draft.livekitOutboundPattern,
      "Outbound pattern",
    );
    return {
      agent_name: draft.livekitAgentName.trim(),
      audio: null,
      credentials_ref: draft.livekitCredentialsRef.trim(),
      data: {
        agent_end_signal: {
          equals: draft.livekitAgentEndEquals.trim(),
          field: draft.livekitAgentEndField.trim(),
        },
        inbound: {
          echo_filter: null,
          text_path: draft.livekitInboundTextPath.trim() || null,
          type_field: null,
          type_values: null,
        },
        max_message_bytes: Number(draft.livekitMaxMessageBytes) || 15_360,
        outbound_pattern: outbound.ok ? outbound.value : {},
        topic: draft.livekitTopic.trim(),
      },
      provider: "livekit",
      server_url: draft.livekitServerUrl.trim(),
    };
  }

  if (draft.interfaceKind === "pipecat_voice") {
    const body = parseOptionalJsonObject(
      draft.pipecatBody,
      "Initialization body",
    );
    return {
      agent_name: draft.pipecatAgentName.trim(),
      api_key_ref: draft.pipecatApiKeyRef.trim(),
      body: body.ok ? body.value : null,
      provider: "pipecat_cloud",
    };
  }

  if (draft.interfaceKind === "pipecat_chat") {
    const body = parseOptionalJsonObject(
      draft.pipecatBody,
      "Initialization body",
    );
    const outbound = parseJsonObject(
      draft.pipecatOutboundPattern,
      "Outbound pattern",
    );
    return {
      agent_end_signal: {
        equals: draft.pipecatAgentEndEquals.trim(),
        field: draft.pipecatAgentEndField.trim(),
      },
      agent_name: draft.pipecatAgentName.trim(),
      api_key_ref: draft.pipecatApiKeyRef.trim(),
      body: body.ok ? body.value : null,
      inbound: {
        echo_filter: null,
        text_path: draft.pipecatInboundTextPath.trim(),
        type_field: null,
        type_values: null,
      },
      outbound_pattern: outbound.ok ? outbound.value : {},
      provider: "pipecat_cloud",
    };
  }

  return {
    account_sid: draft.accountSid.trim(),
    auth_token_secret_ref: draft.authTokenSecretRef.trim(),
    codec: null,
    from_number: draft.fromNumber.trim(),
    provider: "twilio",
    signaling_headers: null,
    to_number: draft.toNumber.trim(),
    trigger: null,
  };
}
